import React, { type FC, useCallback, useState } from 'react'
import { Box, Dialog, type SxProps, type Theme } from '@mui/material'
import { type DateRange } from 'react-day-picker'
import 'react-day-picker/style.css'

import { formatTime, isSameCalendarDay, mergeDateTime, type ReadonlyDate, toEndOfMinute } from '../../../../utils'

import { ActionButtons } from './ActionButtons'
import { Calendar, type CalendarProps } from './Calendar'
import { TimeInputs } from './TimeInputs'

export type DateTimeRangeValue = Readonly<{
    /**
     * `null` means an open-started range: "up to `end`" with no lower bound. This picker's own `onApply` output
     * always has at least one of `start`/`end` non-null, but a caller's idle/unfiltered state (both `null`) is a
     * valid value of this type too - see `Logs/Table.tsx`.
     */
    start: ReadonlyDate | null
    /** `null` means an open-ended range: "from `start` onward" with no upper bound. See {@link start} for both-null. */
    end: ReadonlyDate | null
}>

type DateTimeRangePickerProps =
    & Readonly<{
        value?: DateTimeRangeValue
        onApply: (value: DateTimeRangeValue) => void
        onCancel: () => void
    }>
    & Pick<CalendarProps, 'disableAfter'>

const containerStyle: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    maxWidth: 625,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: 8,
    overflow: 'hidden',
}

const dividerStyle: SxProps<Theme> = {
    height: '1px',
    width: '100%',
    bgcolor: 'divider',
}

const footerStyle: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 3,
    padding: 3,
}

// The times a freshly-picked day's field defaults to, so the user doesn't have to type a time just to enable
// Apply - still fully editable before applying. The start begins its day and the end closes it, so a range includes its end day in full.
const DEFAULT_START_TIME = '00:00'
const DEFAULT_END_TIME = '23:59'

// An end day of today can't default past the current time, which is the latest the picker accepts
const defaultEndTime = (day: ReadonlyDate, now: ReadonlyDate): string => (isSameCalendarDay(day, now) ? formatTime(now) : DEFAULT_END_TIME)

export const DateTimeRangePicker: FC<DateTimeRangePickerProps> = ({ value, onApply, onCancel, disableAfter }) => {
    const [range, setRange] = useState<DateRange | undefined>(
        value ? { from: (value.start as Date | null) ?? undefined, to: (value.end as Date | null) ?? undefined } : undefined
    )
    const [displayMonth, setDisplayMonth] = useState(() => {
        const date = value?.start ?? value?.end ?? new Date()
        return new Date(date.getFullYear(), date.getMonth(), 1)
    })
    const [times, setTimes] = useState({
        start: value?.start ? formatTime(value.start) : '',
        end: value?.end ? formatTime(value.end) : '',
    })

    const now = new Date()

    const start = range?.from && times.start ? mergeDateTime(range.from, times.start) : undefined
    const end = range?.to && times.end ? mergeDateTime(range.to, times.end) : undefined

    const isSameStartEndDay = range?.from && range?.to ? isSameCalendarDay(range.from, range.to) : false
    const endMinTime = isSameStartEndDay ? times.start : undefined

    // Each present bound must not be in the future, and start can't be after end
    const canApply = (start !== undefined || end !== undefined) &&
        (start === undefined || start <= now) &&
        (end === undefined || end <= now) &&
        (start === undefined || end === undefined || start <= end)

    const handleApply = (): void => {
        // The end time is shown as HH:mm, so the range includes its whole last minute
        onApply({ start: start ?? null, end: end ? toEndOfMinute(end) : null })
    }

    // Stable across renders triggered by typing in the time inputs, so `Calendar` (wrapped in React.memo)
    // doesn't re-render its whole two-month grid on every keystroke
    const handleRangeSelect = useCallback(
        (newRange: DateRange | undefined): void => {
            // A new start day discards any previously typed start time - it belonged to a different day. This
            // covers a fresh anchor, a full range collapsing back to just a start day (react-day-picker's own
            // reset-on-reselect, which keeps `from` anchored to the clicked day even when it matches the previous
            // range's start), and a range "swap" (clicking a day before the current anchor makes that earlier day
            // the new start and the current anchor the new end, in one click) - the new start day is a day that
            // never had a start time typed for it, so it still gets a fresh default.
            const isReset = range?.to !== undefined && newRange?.to === undefined
            const startDayChanged = isReset || newRange?.from?.getTime() !== range?.from?.getTime()

            // A freshly-picked start day (or a discarded one, per above) defaults to the start of its day, not
            // reapplied over a time the user already typed (including deliberately clearing it back to blank).
            // The end time always gets its default: every click either wipes `to` or freshly defines it (see
            // `useRange.select` in react-day-picker). On a swap the old anchor becomes the end day, and gets the
            // end's default like any other end day, not the start time it had.
            setTimes((t) => ({
                start: newRange?.from ? (startDayChanged ? DEFAULT_START_TIME : t.start) : '',
                end: newRange?.to ? defaultEndTime(newRange.to, new Date()) : '',
            }))

            setRange(newRange)
        },
        [range]
    )

    return (
        // Dialog provides modal overlay and focus management for the date time range picker
        <Dialog open onClose={onCancel} maxWidth={false}>
            <Box sx={containerStyle}>
                <Calendar
                    selectedRange={range}
                    onRangeSelect={handleRangeSelect}
                    displayMonth={displayMonth}
                    onMonthChange={setDisplayMonth}
                    disableAfter={disableAfter}
                />

                <Box sx={dividerStyle} />

                <Box sx={footerStyle}>
                    <TimeInputs
                        startTime={times.start}
                        endTime={times.end}
                        startDate={range?.from}
                        endDate={range?.to}
                        endMinTime={endMinTime}
                        now={now}
                        onStartTimeChange={(s) => setTimes((t) => ({ ...t, start: s }))}
                        onEndTimeChange={(e) => setTimes((t) => ({ ...t, end: e }))}
                    />

                    <ActionButtons onCancel={onCancel} onApply={handleApply} disabled={!canApply} />
                </Box>
            </Box>
        </Dialog>
    )
}
